/**
 * The "your password needs to be reset" message on `/login`, with a "reset your password" link. The
 * copy is `CONSTANTS[language].RESET_MESSAGE`, `RESET_PASSWORD` and `RESET_MESSAGE2` from
 * `@ryerson/frontend.common`.
 *
 * Shown by `components/Login/LoginHelpers/Login.Components.tsx` → `ErrorMessage` when its
 * `apiErrorMessage` is `LoginErrorResponse.Reset` (`enums/common.enums.ts`). The caller,
 * `components/Login/LoginHelpers/LoginForm.tsx` → `onLoginClick`, passes the rejected sign-in's
 * `error.data`, which is undefined for every rejection the shared `login` produces. So this message
 * isn't shown today, and a `Reset` sign-in shows the generic `AUTH_ERROR` alert instead. The
 * counterpart for a locked account is `LockOutErrorMessage.tsx`.
 */
import { Icon } from "@ryerson/frontend.assets";
import { CONSTANTS } from "@ryerson/frontend.common";
import { Flex } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";
import { useTheme } from "@ryerson/frontend.theme";
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import { Link } from "@ryerson/frontend.navigation";

/**
 * Renders the reset-required message.
 *
 * `forgotPasswordUrl` is a local `let`, reset to `""` on every render, and the effect assigns
 * `<origin>/login/forgot-password` to it only after the render that uses it. The value passed to
 * `Link`'s `to` is therefore always `""`, and the link doesn't lead to `/login/forgot-password`.
 */
const ResetErrorMessage: React.FC = () => {
	const { theme } = useTheme();
	const {
		localization: { language },
	} = useApplication();

	const [hasMounted, setHasMounted] = React.useState<boolean>(false);

	let forgotPasswordUrl: string = "",
		origin: string = "";

	React.useEffect(() => {
		if (hasMounted && !!window) {
			origin = window?.location?.origin;
			forgotPasswordUrl = !!window ? origin + "/login/forgot-password" : "";
		}
	}, [hasMounted]);

	React.useEffect(() => {
		setHasMounted(true);
	}, []);

	return (
		<Flex css={{ gap: 8, marginTop: "20px" }} alignItems="center">
			<Icon icon="exclamation" color={theme.colors.primary.accentBrand} circled={true} />
			<Flex direction="column">
				<Typography variant="span" size="sm" color={theme.colors.primary.secondaryBrand}>
					<span>{`${CONSTANTS[language].RESET_MESSAGE} `}</span>
					<div>
						<Link type="secondary" to={forgotPasswordUrl} size="sm">
							{`${CONSTANTS[language].RESET_PASSWORD} `}
						</Link>{" "}
						{CONSTANTS[language].RESET_MESSAGE2}
					</div>
				</Typography>
			</Flex>
		</Flex>
	);
};

export default ResetErrorMessage;
